'use client';

import { useState } from 'react';
import { Cloud, Download, Save, X } from 'lucide-react';
import { useEditorStore } from '@/lib/editor/editorStore';
import type { EditorExportFps, EditorExportOptions, EditorExportResolution } from '@/types/editor';
import { cn } from '@/lib/utils/cn';

const TOOL_LABELS: Record<string, string> = {
  media: 'Медиа', text: 'Текст', stickers: 'Стикеры', effects: 'Эффекты',
  audio: 'Аудио', transitions: 'Переходы', captions: 'Субтитры',
  filters: 'Фильтры', templates: 'Шаблоны',
};

export interface EditorTopBarProps {
  onRender: (options: EditorExportOptions) => void;
  onSave?: () => void;
  onImport?: () => void;
  onSplit?: () => void;
  timecode?: string;
  rendering?: boolean;
}

/**
 * Top bar — faithful to the Editor V2 concept: left = «← Проекты» + title +
 * timecode; center = dot-separated category tabs (active underlined); right =
 * «Сохранить» + solid violet «Рендер». Import/Split live in the Media panel /
 * timeline toolbar, so the header stays to just these two actions.
 */
export function EditorTopBar({ onRender, onSave, rendering }: EditorTopBarProps) {
  const activeTool = useEditorStore((s) => s.activeTool);
  const saving = useEditorStore((s) => s.saving);
  const [exportOpen, setExportOpen] = useState(false);
  const [resolution, setResolution] = useState<EditorExportResolution>('1080p');
  const [fps, setFps] = useState<EditorExportFps>(30);

  const startRender = () => {
    setExportOpen(false);
    onRender({ resolution, fps });
  };

  return (
    <header className="relative flex min-h-14 min-w-0 items-center gap-4 border-b border-white/10 bg-[#0b0b0c] px-4 py-2.5">
      {/* LEFT: title (back arrow lives in the full-height rail now) */}
      <div className="flex shrink-0 items-center">
        <span className="whitespace-nowrap text-sm font-semibold text-white">Редактор видео</span>
        <span className="mx-2 text-white/20">/</span>
        <span className="text-xs font-medium text-white/45">{TOOL_LABELS[activeTool]}</span>
      </div>

      <div className="flex min-w-0 flex-1 items-center justify-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-[11px] font-medium text-white/45">
          <Cloud className="h-3.5 w-3.5" /> {saving ? 'Сохраняем…' : 'Изменения сохраняются автоматически'}
        </span>
      </div>

      {/* RIGHT: Сохранить + Рендер */}
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-white/15 px-3 py-2 text-xs font-medium text-white/90 transition hover:bg-white/10"
        >
          <Save className="h-3.5 w-3.5" />
          Сохранить
        </button>
        <button
          type="button"
          onClick={() => setExportOpen((open) => !open)}
          disabled={rendering}
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#168cff] px-4 py-2 text-xs font-semibold text-white shadow-[0_0_0_1px_rgba(255,255,255,0.18)_inset] transition hover:bg-[#2a98ff] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" /> Экспорт
        </button>
      </div>

      {exportOpen && (
        <div className="absolute right-4 top-[calc(100%+8px)] z-[90] w-[300px] rounded-2xl border border-white/15 bg-[#18181b] p-4 shadow-2xl shadow-black/60">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-white">Экспорт видео</p>
              <p className="text-[10px] text-white/40">Параметры итогового файла</p>
            </div>
            <button
              type="button"
              onClick={() => setExportOpen(false)}
              aria-label="Закрыть настройки экспорта"
              className="rounded-lg p-1.5 text-white/45 hover:bg-white/10 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <ExportChoice
            label="Разрешение"
            values={['720p', '1080p', '4k'] as const}
            value={resolution}
            onChange={setResolution}
          />
          <ExportChoice
            label="Частота кадров"
            values={[24, 30, 60] as const}
            value={fps}
            format={(value) => `${value} fps`}
            onChange={setFps}
          />
          <button
            type="button"
            onClick={startRender}
            disabled={rendering}
            className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#168cff] text-xs font-bold text-white transition hover:bg-[#2a98ff] disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Экспортировать {resolution} · {fps} fps
          </button>
        </div>
      )}
    </header>
  );
}

function ExportChoice<T extends string | number>({
  label,
  values,
  value,
  format,
  onChange,
}: {
  label: string;
  values: readonly T[];
  value: T;
  format?: (value: T) => string;
  onChange: (value: T) => void;
}) {
  return (
    <div className="mt-3">
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-white/40">{label}</p>
      <div className="grid grid-cols-3 gap-1.5">
        {values.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            className={cn(
              'h-8 rounded-lg border text-[11px] font-semibold transition',
              item === value
                ? 'border-brand-400 bg-brand-500/20 text-white'
                : 'border-white/10 bg-white/5 text-white/50 hover:text-white'
            )}
          >
            {format ? format(item) : item}
          </button>
        ))}
      </div>
    </div>
  );
}
